/**
 * Robust logging utility with performance-aware debug logging
 * Based on patterns from Vitest, React, TanStack Router, and other major TypeScript projects
 */

import type { Debugger as DebugInstance } from "debug";
import createDebug from "debug";

type DebugFactory = (namespace: string) => DebugInstance;

// Brand types for type safety
type LogCategory = string & { readonly __logCategoryBrand: never };
type LogNamespace = `tony:${string}`;

// Core interfaces
export interface LoggerOptions {
  namespace: LogNamespace;
  enablePerformanceTracking?: boolean;
  enabledCategories?: readonly LogCategory[];
}

export interface TimingResult {
  duration: number;
  label: string;
  result?: unknown;
  error?: unknown;
}

export interface Logger {
  readonly namespace: string;
  readonly enabled: boolean;
  log(message: string, ...args: unknown[]): void;
  category(category: LogCategory, message: string, ...args: unknown[]): void;
  time<T>(label: string, fn: () => T): T;
  timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T>;
  error(error: unknown, context?: Record<string, unknown>): void;
  group(label: string): void;
  groupEnd(): void;
}

// Environment detection utilities
export const Environment = {
  isBrowser: typeof window !== "undefined" && typeof document !== "undefined",
  isCI: import.meta.env.CI === true || import.meta.env.CI === "true",
  isDevelopment: import.meta.env.DEV,
  isNode: false, // Browser-only code, always false
  isProduction: import.meta.env.PROD,
  isTest: import.meta.env.MODE === "test",
} as const;

let debugFactory: DebugFactory = createDebug;

/**
 * Allows tests to override the underlying debug factory.
 * Passing no argument resets it to the default implementation.
 */
export function setDebugFactory(factory?: DebugFactory): void {
  debugFactory = factory ?? createDebug;
}

// Logger implementation
class LoggerImpl implements Logger {
  private readonly debug: DebugInstance | undefined;
  private readonly performanceEnabled: boolean;
  private readonly options: LoggerOptions;
  private groupLevel = 0;

  constructor(options: LoggerOptions) {
    this.options = options;

    // Lazy debug setup - only if enabled to avoid performance overhead
    const debugInstance = debugFactory(options.namespace);
    this.debug = debugInstance.enabled ? debugInstance : undefined;

    this.performanceEnabled = Boolean(
      options.enablePerformanceTracking && this.debug,
    );
  }

  get namespace(): string {
    return this.options.namespace;
  }

  get enabled(): boolean {
    return Boolean(this.debug);
  }

  log(message: string, ...args: unknown[]): void {
    if (!this.debug) return;

    try {
      const indent = "  ".repeat(this.groupLevel);
      this.debug(`${indent}${message}`, ...args);
    } catch (_error) {
      this.fallbackLog("log", message, ...args);
    }
  }

  category(category: LogCategory, message: string, ...args: unknown[]): void {
    if (!this.debug) return;

    // Check if category is enabled (if filtering is configured)
    if (
      this.options.enabledCategories &&
      !this.options.enabledCategories.includes(category)
    ) {
      return;
    }

    try {
      const indent = "  ".repeat(this.groupLevel);
      this.debug(`${indent}[${category}] ${message}`, ...args);
    } catch (_error) {
      this.fallbackLog("log", `[${category}] ${message}`, ...args);
    }
  }

  time<T>(label: string, fn: () => T): T {
    if (!this.performanceEnabled) {
      return fn();
    }

    let start: number;
    try {
      start = performance.now();
    } catch {
      // Fallback if performance.now() fails
      return fn();
    }

    try {
      const result = fn();
      try {
        const duration = performance.now() - start;
        this.debug?.(`⏱️  ${label}: ${duration.toFixed(2)}ms`);
      } catch {
        // Ignore timing errors, still return result
      }
      return result;
    } catch (_error) {
      try {
        const duration = performance.now() - start;
        this.debug?.(`⏱️  ${label} failed after ${duration.toFixed(2)}ms`);
      } catch {
        // Ignore timing errors in error case
      }
      throw _error;
    }
  }

  async timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    if (!this.performanceEnabled) {
      return fn();
    }

    let start: number;
    try {
      start = performance.now();
    } catch {
      // Fallback if performance.now() fails
      return fn();
    }

    try {
      const result = await fn();
      try {
        const duration = performance.now() - start;
        this.debug?.(`⏱️  ${label}: ${duration.toFixed(2)}ms`);
      } catch {
        // Ignore timing errors, still return result
      }
      return result;
    } catch (_error) {
      try {
        const duration = performance.now() - start;
        this.debug?.(`⏱️  ${label} failed after ${duration.toFixed(2)}ms`);
      } catch {
        // Ignore timing errors in error case
      }
      throw _error;
    }
  }

  error(error: unknown, context?: Record<string, unknown>): void {
    const errorInfo = {
      context,
      environment: import.meta.env.MODE || "browser",
      message: error instanceof Error ? error.message : String(error),
      namespace: this.namespace,
      stack: error instanceof Error ? error.stack : undefined,
      timestamp: new Date().toISOString(),
    };

    if (this.debug) {
      try {
        this.debug("❌ ERROR: %O", errorInfo);
      } catch (debugError) {
        this.fallbackLog(
          "error",
          "Logger Error:",
          debugError,
          "Original error:",
          errorInfo,
        );
      }
    } else if (!Environment.isProduction) {
      // In development/test, always show errors even if debug is disabled
      console.error("Logger Error:", errorInfo);
    }
  }

  group(label: string): void {
    if (!this.debug) return;

    try {
      const indent = "  ".repeat(this.groupLevel);
      this.debug(`${indent}▼ ${label}`);
      this.groupLevel++;
    } catch (_error) {
      this.fallbackLog("group", label);
    }
  }

  groupEnd(): void {
    if (!this.debug) return;

    this.groupLevel = Math.max(0, this.groupLevel - 1);
    try {
      const indent = "  ".repeat(this.groupLevel);
      this.debug(`${indent}▲`);
    } catch (_error) {
      this.fallbackLog("log", "Group end");
    }
  }

  private fallbackLog(level: string, ...args: unknown[]): void {
    try {
      const consoleMethod =
        level in console &&
        typeof console[level as keyof Console] === "function"
          ? (console[level as keyof Console] as typeof console.log)
          : console.log;
      consoleMethod(`[${this.namespace}]`, ...args);
    } catch {
      // Silent failure as last resort - never crash the app due to logging
    }
  }
}

// Factory function for creating loggers
export function getLogger(
  namespace: string,
  options: Omit<LoggerOptions, "namespace"> = {},
): Logger {
  // Validate namespace format
  if (!namespace.match(/^[a-z][a-z0-9]*(?::[a-z][a-z0-9]*)*$/)) {
    throw new Error(
      `Invalid namespace format: "${namespace}". Must match pattern: [a-z][a-z0-9]*(?::[a-z][a-z0-9]*)*`,
    );
  }

  const fullNamespace = `tony:${namespace}` as LogNamespace;

  return new LoggerImpl({
    namespace: fullNamespace,
    ...options,
  });
}

// Helper function to create log categories with type safety
export function createLogCategory(category: string): LogCategory {
  if (!category.match(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/)) {
    throw new Error(
      `Invalid category format: "${category}". Must match pattern: [a-z][a-z0-9]*(?:-[a-z0-9]+)*`,
    );
  }
  return category as LogCategory;
}

// Pre-configured loggers for common use cases - lazy initialization for testing
let _navigationLogger: Logger | undefined;
let _sidebarLogger: Logger | undefined;
let _routingLogger: Logger | undefined;
let _themeLogger: Logger | undefined;
let _gemLogger: Logger | undefined;

export const navigationLogger = () => {
  if (!_navigationLogger) {
    _navigationLogger = getLogger("navigation", {
      enablePerformanceTracking: true,
    });
  }
  return _navigationLogger;
};

export const sidebarLogger = () => {
  if (!_sidebarLogger) {
    _sidebarLogger = getLogger("sidebar", {
      enablePerformanceTracking: true,
    });
  }
  return _sidebarLogger;
};

export const routingLogger = () => {
  if (!_routingLogger) {
    _routingLogger = getLogger("routing", {
      enablePerformanceTracking: true,
    });
  }
  return _routingLogger;
};

export const themeLogger = () => {
  if (!_themeLogger) {
    _themeLogger = getLogger("theme");
  }
  return _themeLogger;
};

export const gemLogger = () => {
  if (!_gemLogger) {
    _gemLogger = getLogger("gem", {
      enablePerformanceTracking: true,
    });
  }
  return _gemLogger;
};

// Common log categories
export const LogCategories = {
  API: createLogCategory("api"),
  ERROR: createLogCategory("error"),
  LIFECYCLE: createLogCategory("lifecycle"),
  NAVIGATION: createLogCategory("navigation"),
  PERFORMANCE: createLogCategory("performance"),
  STATE: createLogCategory("state"),
  UI: createLogCategory("ui"),
} as const;

// Export types for external use
export type { DebugFactory, LogCategory, LogNamespace };
